import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// メッセージ画面の自動更新（ポーリング）担当。
// 描画はmorphに任せ、入力欄のdisabled/lock_versionもサーバー値へ追従させる。
// ただし本文欄の中身（下書き）だけはmorphの上書き対象から除外する（preserveComposerDraft）。
export default class extends Controller {
  static targets = ["state"]
  static values = {
    url: String,
    interval: { type: Number, default: 5000 },
    maxConsecutiveFailures: { type: Number, default: 3 }
  }

  connect() {
    this.busy = false
    this.consecutiveFailureCount = 0
    this.timer = setInterval(() => this.poll(), this.intervalValue)
    document.addEventListener("visibilitychange", this.handleVisibilityChange)
    this.element.addEventListener("turbo:before-morph-attribute", this.preserveComposerDraft)
  }

  disconnect() {
    this.#stopPolling()
    this.element.removeEventListener("turbo:before-morph-attribute", this.preserveComposerDraft)
  }

  // pollのmorphは入力欄も対象にしているため、他ユーザーの書きかけの本文が
  // サーバー側の値（未入力）で上書きされてしまう。本文欄のvalue更新だけ止める。
  preserveComposerDraft = (event) => {
    if (event.detail.attributeName === "value" && event.target instanceof HTMLTextAreaElement) {
      event.preventDefault()
    }
  }

  // バックグラウンドタブでは停止、フォアグラウンド復帰時に即時ポーリング。
  handleVisibilityChange = () => {
    if (document.visibilityState === "visible") this.poll()
  }

  async poll() {
    if (this.busy || document.visibilityState === "hidden") return
    this.busy = true

    try {
      // "token" は会社切り替え用に予約済みのため "state_token" を使う。
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("state_token", this.stateTarget.dataset.token ?? "")

      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" }
      })

      if (!response.ok) {
        this.#registerFailure()
        return
      }

      this.consecutiveFailureCount = 0
      if (response.status === 204) return

      const html = await response.text()
      // fetch中にページ遷移でdisconnectした場合、別ページのDOMへ当てないよう中断
      if (!this.element.isConnected) return
      Turbo.renderStreamMessage(html)

      // スクロール判定に実際の反映高さが必要なため、morph完了を待機。
      await this.#waitForRepaint()
      this.#scrollToBottomIfNeeded()
    } catch (_e) {
      // ネットワークエラーは一定回数連続した場合のみポーリングを停止
      this.#registerFailure()
    } finally {
      this.busy = false
    }
  }

  #registerFailure() {
    this.consecutiveFailureCount += 1
    if (this.consecutiveFailureCount >= this.maxConsecutiveFailuresValue) this.#stopPolling()
  }

  #stopPolling() {
    clearInterval(this.timer)
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  // Turboのmorph描画完了待ち（余裕を持たせ2フレーム待機）。
  #waitForRepaint() {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  }

  #scrollToBottomIfNeeded() {
    const content = this.element.querySelector(".selection-message-page__content")
    if (!content) return
    const distanceFromBottom = content.scrollHeight - content.scrollTop - content.clientHeight
    if (distanceFromBottom < 100) content.scrollTop = content.scrollHeight
  }
}
